import type { QueryClient } from '@tanstack/react-query'

import { UNREAD_COUNT_KEY } from './use-unread-count'

/** Badging API wrapper. only meaningful for installed PWAs (mac/win
 * desktop installs + iOS 16.4+ + android). on regular browser tabs the
 * call is a no-op so feature-detection is the only gate.
 *
 * called from socket-handlers.ts whenever the unread count might have
 * changed. we re-read the latest value from the TanStack Query cache
 * to stay consistent with the in-app bell.
 */

type NavigatorWithBadge = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>
  clearAppBadge?: () => Promise<void>
}

function getBadgeApi(): NavigatorWithBadge | null {
  if (typeof navigator === 'undefined') return null
  const nav = navigator as NavigatorWithBadge
  if (typeof nav.setAppBadge !== 'function') return null
  return nav
}

export function setAppBadge(queryClient: QueryClient): void {
  const api = getBadgeApi()
  if (!api) return
  const data = queryClient.getQueryData<{ count: number }>(UNREAD_COUNT_KEY)
  const count = data?.count ?? 0
  if (count > 0) {
    void api.setAppBadge?.(count)
  } else {
    void api.clearAppBadge?.()
  }
}

export function clearAppBadge(): void {
  const api = getBadgeApi()
  if (!api) return
  void api.clearAppBadge?.()
}
